import { toMoney } from '@utils/Format'

/**
 * O anexo de carteira na conversa do guia (spec-004).
 *
 * Aqui moram os limites e a régua de espera que o card e o modal do anexo
 * compartilham. Duas cópias (uma no `accept` do UploadFileV2, outra na
 * validação da classe de API) divergiriam no primeiro formato novo — e a
 * divergência silenciosa é o consultor conseguir escolher um arquivo que a
 * própria tela recusa em seguida.
 *
 * ⚠️ Os limites são os do BACKEND (`usecase_fee_fixo_carteira_chat`), repetidos
 * aqui só para o consultor saber ANTES de subir 20 MB por uma rede móvel. Quem
 * decide continua sendo o servidor: mudou lá, muda aqui no mesmo PR.
 */

// Os estados que o backend publica em `fee_fixo_carteira_upload.status`
// (`core/carteira_chat_upload_registro.py`). Transcritos, não deduzidos.
export const ESTADO_PROCESSANDO = 'processando'
export const ESTADO_PREVIEW = 'preview'
export const ESTADO_GRAVADO = 'gravado'
export const ESTADO_ERRO = 'erro'
export const ESTADO_CANCELADO = 'cancelado'

/**
 * O sexto estado é só do FRONT: entre o clique em "Enviar" e a primeira
 * resposta do polling não existe linha no banco para consultar, e um card em
 * branco nesse intervalo é indistinguível de tela travada.
 */
export const ESTADO_ENVIANDO = 'enviando'

/** Onde a espera acaba — em qualquer um deles o polling PARA. */
export const ESTADOS_TERMINAIS = [
  ESTADO_PREVIEW,
  ESTADO_GRAVADO,
  ESTADO_ERRO,
  ESTADO_CANCELADO,
]

// Extensões aceitas (contrato §1). Formato de `UploadFileV2.types`.
export const EXTENSOES_ACEITAS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.xlsx',
  '.xls',
  '.csv',
]

export const TAMANHO_MAXIMO_MB = 20
export const MAX_ARQUIVOS = 10

const BYTES_POR_MB = 1024 * 1024

/** A extensão do nome, em minúsculas e com o ponto — o formato da lista. */
const extensaoDe = (arquivo) => {
  const nome = String((arquivo && arquivo.name) || '')
  const ponto = nome.lastIndexOf('.')
  return ponto === -1 ? '' : nome.slice(ponto).toLowerCase()
}

/**
 * Backoff da consulta de status.
 *
 * A extração passa por OCR de terceiro e leva de ~30 s a poucos minutos.
 * Intervalo fixo curto significaria dezenas de requisições por leitura, e
 * intervalo fixo longo faria o preview demorar a aparecer depois de pronto: o
 * backoff é o meio-termo — rápido no começo, espaçado quando a espera se
 * arrasta.
 */
export const POLLING_BASE_MS = 2000
export const POLLING_FATOR = 1.6
export const POLLING_TETO_MS = 10000

/**
 * O quanto a TELA espera. Não é o quanto o servidor demora: estourado o teto,
 * a leitura continua lá — o card diz isso e oferece retomar o acompanhamento
 * com o MESMO `upload_id`. Anunciar "falhou" mandaria o consultor reenviar o
 * arquivo e gastar uma segunda extração.
 */
export const TETO_ESPERA_MS = 300000

/**
 * O intervalo da próxima consulta, dada a tentativa já feita (1 = a primeira).
 *
 * Função (e não uma lista de valores) porque a espera não tem número fixo de
 * ciclos: quem a encerra é o teto de relógio.
 */
export const intervaloDePolling = (tentativa) => {
  const expoente = Math.max(0, (tentativa || 1) - 1)
  const bruto = POLLING_BASE_MS * Math.pow(POLLING_FATOR, expoente)
  return Math.min(Math.round(bruto), POLLING_TETO_MS)
}

/**
 * As colunas do preview, na ordem do wireframe (Ativo · Tipo · Classe · Valor ·
 * Custo).
 *
 * `classe_simulacao` — e não `classe` — porque é ela que decide o take rate
 * aplicado na simulação; o rótulo é o MESMO da tela de importação da carteira,
 * para o consultor conferir a mesma coisa nos dois lugares. `valor_label` e
 * `custo_label` são os campos já formatados pelo card, por `rotuloDoCusto`
 * abaixo (mora aqui, e não no `.vue`, pelo mesmo limite de 500 linhas do
 * repo que já rege o resto deste arquivo).
 */
export const COLUNAS_DO_PREVIEW = [
  { text: 'Ativo', value: 'nome_ativo', align: 'start' },
  { text: 'Tipo', value: 'tipo', align: 'start' },
  { text: 'Classe simulação', value: 'classe_simulacao', align: 'start' },
  { text: 'Valor', value: 'valor_label', align: 'end' },
  { text: 'Custo estimado', value: 'custo_label', align: 'end' },
]

const SEM_ESTIMATIVA = '—'
const UNIDADE_CUSTO = 'bps'

/** `null`/`undefined`/`''` é AUSÊNCIA de estimativa (travessão); `0` é custo
 * REAL de uma ação (US-5) — a razão de a coluna existir. */
export const rotuloDoCusto = (bps) => {
  if (bps === null || bps === undefined || bps === '') return SEM_ESTIMATIVA
  return `${bps} ${UNIDADE_CUSTO}`
}

/**
 * A origem da linha da carteira anterior, por extenso.
 *
 * O backend publica exatamente TRÊS chaves no `plano_zeramento`: `ficticio` e
 * `externa` (`core/zeramento_carteira.py:40-41`) e `propria`, posta pelo
 * `plano_para_o_card` na linha que ESTA importação removeu
 * (`carteira_chat_upload_registro.py:274`). O texto de `externa` é a própria
 * definição do backend ("tudo que o Fee Fixo não escreveu: consolidador,
 * crawler, digitação em outra tela") — não invente uma mais específica: a
 * chave não distingue essas três procedências.
 *
 * Chave nova sai CRUA na tela (ver `rotuloDaOrigem`), nunca `—` nem omitida:
 * item novo do plano precisa aparecer feio em vez de desaparecer. Mesmo
 * padrão do catálogo da esteira (`constants/esteira.js`).
 */
export const ROTULO_DA_ORIGEM = {
  // Só de ONDE veio: o nome do ativo já está na frase, e o fictício do
  // positivador costuma se chamar "Custódia" — "Custódia … (origem: Custódia
  // do positivador)" dizia a mesma palavra duas vezes. Medido na captura.
  ficticio: 'Positivador',
  externa: 'Consolidador, crawler ou outra tela',
  propria: 'Envio anterior por esta conversa',
}

export const rotuloDaOrigem = (origem) =>
  ROTULO_DA_ORIGEM[origem] || origem || ''

/**
 * A recusa dos arquivos escolhidos — vazia quando está tudo dentro do limite.
 *
 * Fonte ÚNICA da régua no front: a chama a camada de API (antes de a
 * requisição sair) e o modal (na hora da seleção, para o consultor descobrir
 * ali e não depois de subir 20 MB por uma rede móvel). Duas cópias
 * divergiriam no primeiro formato novo, e a tela passaria a aceitar o que o
 * servidor recusa — ou o contrário. O servidor continua sendo a autoridade.
 *
 * A frase NOMEIA o arquivo: com dez anexos, "algum passou do limite" faz o
 * consultor tentar de novo às cegas.
 */
export const recusaDosArquivos = (arquivos) => {
  const lista = arquivos || []
  if (lista.length > MAX_ARQUIVOS) {
    return (
      `Envie no máximo ${MAX_ARQUIVOS} arquivos por vez — você escolheu ` +
      `${lista.length}. Separe em dois envios.`
    )
  }
  for (const arquivo of lista) {
    const nome = (arquivo && arquivo.name) || 'arquivo sem nome'
    if (EXTENSOES_ACEITAS.indexOf(extensaoDe(arquivo)) === -1) {
      return (
        `"${nome}" não é um formato que eu leia. Envie ` +
        `${EXTENSOES_ACEITAS.join(', ')}.`
      )
    }
    if (((arquivo && arquivo.size) || 0) > TAMANHO_MAXIMO_MB * BYTES_POR_MB) {
      return (
        `"${nome}" passa do limite de ${TAMANHO_MAXIMO_MB} MB por arquivo. ` +
        'Envie uma versão menor ou separe as páginas.'
      )
    }
  }
  return ''
}

/**
 * A copy do card, junto do resto do vocabulário deste fluxo.
 *
 * Mora aqui, e não no `.vue`, pela regra do módulo (texto de tela em
 * `constants/`) e porque o componente vive no limite de 500 linhas do repo:
 * frase nova entra aqui, não lá.
 */
export const MSG_CANCELADO =
  'Nada foi gravado na carteira. Anexe outro extrato quando quiser.'

export const MSG_PROCESSANDO =
  'Extraindo os ativos e estimando o custo de cada um. Costuma levar de 1 a 2 minutos.'

export const MSG_ENVIANDO = 'Enviando os arquivos para leitura.'

/**
 * O sétimo estado do cartão — só de apresentação, sem valor novo no banco
 * (spec-008 US-6, `contracts/ui-components.md` §2): extrato lido, mas
 * nenhuma posição pôde ser importada. Sem ele, o título genérico ("Carteira
 * extraída") mentia — dizia que havia carteira quando o lote estava vazio.
 */
export const TITULO_LIDO_SEM_POSICOES = 'Extrato lido, nenhuma posição importada'
export const MSG_LIDO_SEM_POSICOES =
  'Li o arquivo e nenhuma das posições pôde ser importada. Os motivos estão abaixo.'

/**
 * A linha de conferência do resumo (spec-008 US-4, `data-model.md` §3.2):
 * soma do que o documento tem × total que ele mesmo declara. Ausência de
 * `conferencia` (nenhuma via certificou o total) não é traço nem zero — a
 * função devolve string vazia e a linha simplesmente não é desenhada.
 */
export const ROTULO_CONFERE = 'bate'
export const ROTULO_NAO_CONFERE = 'NÃO bate'

export const montarLinhaDeConferencia = (conferencia) => {
  if (!conferencia) return ''
  const base =
    `Soma das posições ${toMoney(conferencia.soma_posicoes)} · ` +
    `total declarado no documento ${toMoney(conferencia.total_declarado)}`
  return conferencia.bate
    ? `${base} · ${ROTULO_CONFERE}`
    : `${base} · diferença ${toMoney(conferencia.diferenca)} — ${ROTULO_NAO_CONFERE}`
}

/** A cor da linha: `az-muted` quando bate, `ff-carteira-upload__atencao`
 * (já existe no `.vue`) quando não bate. */
export const classeDaConferencia = (conferencia) =>
  conferencia && conferencia.bate ? 'az-muted' : 'ff-carteira-upload__atencao'

/**
 * [G-2, review spec-008] O rótulo de uma posição descartada, nunca um
 * travessão órfão. `nao_importaveis[]` (`carteira_chat_ponte.py:459`) traz
 * `{nome: str|null, valor: number|null, motivo}` — `nome` é `null` quando a
 * cadeia de resolução falhou de verdade (US-3 AC3), e o fallback nomeado
 * evita o "— motivo" sem nada antes do travessão que o golden real (29
 * posições sem nome) expunha.
 *
 * ★ `valor` distingue `null` (não apurado) de `0` (posição real zerada) —
 * `!= null` é a checagem certa, `truthy` esconderia um valor de R$ 0,00
 * legítimo. Ausente, o rótulo não inventa um número.
 *
 * ★ `valor` deixou de ser o único
 * campo de valor do descarte. O backend
 * (`aawz_fee_fixo/core/carteira_chat_descarte.py`) separou o papel dos dois
 * números: `valor` é SÓ o certificado (a régua anti-fabricação aceitou, pode
 * vir `null`, nunca fabricado — é o único que a conferência soma) e
 * `valor_lido_nao_certificado` é o número que o MODELO leu e a régua
 * recusou, só presente quando existe um a mostrar. Antes desta chave, um
 * `valor` recusado virava rótulo SEM número — o consultor perdia o valor
 * lido inteiro, mesmo quando havia um plausível para conferir manualmente.
 * O rótulo do "só lido" é DISTINTO do certificado de propósito ("não
 * conferido" ao lado do valor): ele nunca pode ser lido como o valor que a
 * régua aprovou. `typeof === 'number'` (não `truthy`) pela mesma razão do
 * `!= null` acima — R$ 0,00 lido e não certificado ainda é um número a
 * mostrar, não uma ausência.
 */
export const POSICAO_SEM_NOME = 'Posição sem nome'

export const CHAVE_VALOR_LIDO_NAO_CERTIFICADO = 'valor_lido_nao_certificado'

const SUFIXO_VALOR_NAO_CERTIFICADO = 'não conferido'

export const rotuloDoDescarte = (item) => {
  const nome = (item && item.nome) || POSICAO_SEM_NOME
  if (item && item.valor != null) {
    return `${nome} (${toMoney(item.valor)})`
  }
  const valorLido = item && item[CHAVE_VALOR_LIDO_NAO_CERTIFICADO]
  if (typeof valorLido === 'number') {
    return `${nome} (${toMoney(valorLido)}, ${SUFIXO_VALOR_NAO_CERTIFICADO})`
  }
  return nome
}

/**
 * O aviso do preview sem registro no servidor.
 *
 * Copy PRÓPRIA: o `aviso` do envelope também é user-facing neste fluxo
 * (`carteira_chat_upload_registro.MSG_PREVIEW_NAO_REGISTRADO`) e por isso é
 * preferido quando vem — mas ele pode faltar, e botão desabilitado sem
 * explicação é pior que texto genérico.
 */
export const AVISO_SEM_REGISTRO =
  'Não consegui registrar este preview. Anexe o extrato de novo — confirmar ' +
  'agora seria recusado.'

const ACAO_REMOVER = 'remover'

/**
 * O efeito de UMA linha da carteira anterior, em português.
 *
 * Função pura (recebe o item, devolve a frase) e fora do `.vue` por duas
 * razões: é COPY, que neste módulo mora em `constants/`, e assim ela é
 * testável sem montar componente.
 *
 * A linha REMOVIDA é de uma importação anterior deste mesmo caminho — por
 * isso some em vez de ficar zerada (regra de origem da spec-019). A ZERADA
 * nomeia de onde veio, por extenso.
 */
export const fraseDoPlano = (item) => {
  const nome = (item && item.nome_ativo) || 'Posição sem nome'
  const valor =
    typeof (item && item.pl_anterior) === 'number'
      ? ` (${toMoney(item.pl_anterior)})`
      : ''
  if (item && item.acao === ACAO_REMOVER) {
    return `${nome}${valor} será removida — veio de um envio anterior seu.`
  }
  const origem =
    item && item.origem ? ` (origem: ${rotuloDaOrigem(item.origem)})` : ''
  return `${nome}${valor} será zerada, com a linha preservada${origem}.`
}
